import React, { useState, useEffect } from "react";
import {
  FiX,
  FiSave,
  FiClock,
  FiMail,
  FiMessageSquare,
  FiCheckCircle,
  FiBell,
} from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import { toast } from "react-toastify";

// Student gate settings for Gate Monitoring. Staff settings (lateness penalty) moved to
// StaffClockInSettingsModal on the Teacher Attendance page; this modal saves only the student
// part, and the backend keeps the staff part as it is.
const ClockInOutSettingsModal = ({ isOpen, onClose, schoolId, refreshSchool }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(false);
  const [settings, setSettings] = useState({
    auto_mark_attendance: false,
    notify_parents_enabled: false,
    notify_parents_method: "email",
    time_restriction_enabled: false,
    time_restriction_start_hour: 5,
    time_restriction_end_hour: 12,
  });

  useEffect(() => {
    if (isOpen) {
      // Fetch fresh settings each time modal opens
      fetchCurrentSettings();
    }
  }, [isOpen]);

  const fetchCurrentSettings = async () => {
    try {
      setFetchingSettings(true);
      const { data } = await userRequest.get(`/schools/${schoolId}`);

      const currentStudentSettings = data?.settings?.clock_in?.student;

      setSettings({
        auto_mark_attendance: currentStudentSettings?.auto_mark_attendance || false,
        notify_parents_enabled: currentStudentSettings?.notify_parents?.enabled || false,
        notify_parents_method: currentStudentSettings?.notify_parents?.method || "email",
        time_restriction_enabled: currentStudentSettings?.time_restriction?.enabled || false,
        time_restriction_start_hour: currentStudentSettings?.time_restriction?.start_hour ?? 5,
        time_restriction_end_hour: currentStudentSettings?.time_restriction?.end_hour ?? 12,
      });
    } catch (error) {
      console.error("Error fetching current settings:", error);
      toast.error("Failed to load current settings");
    } finally {
      setFetchingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);

      const payload = {
        student: {
          auto_mark_attendance: settings.auto_mark_attendance,
          notify_parents: {
            enabled: settings.notify_parents_enabled,
            method: settings.notify_parents_method,
          },
          time_restriction: {
            enabled: settings.time_restriction_enabled,
            start_hour: parseInt(settings.time_restriction_start_hour, 10),
            end_hour: parseInt(settings.time_restriction_end_hour, 10),
          },
        },
      };

      await userRequest.patch(`/schools/${schoolId}/settings/clock-in`, payload);

      toast.success("Clock in/out settings updated successfully!");

      // Refresh school data in context to get updated settings
      if (refreshSchool) {
        await refreshSchool();
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error updating clock-in settings:", error);
      toast.error(error.response?.data?.message || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl flex flex-col max-h-[90vh] shadow-2xl">
        {/* Header — sticky */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <FiClock className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-base md:text-xl font-bold text-slate-800">
                  Clock In/Out Settings
                </h2>
                <p className="text-slate-500 text-xs md:text-sm">
                  Configure gate monitoring for students
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:bg-slate-200 p-2 rounded-lg transition-colors flex-shrink-0"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content — scrollable */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {fetchingSettings ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
                <p className="text-slate-600 text-sm">Loading current settings...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Section: Student Attendance Settings Header */}
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 pt-1 pb-1">
                Student Settings
              </div>

              {/* Auto Mark Attendance */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FiCheckCircle className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-slate-800">Auto Mark Attendance</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      Automatically mark class attendance when students scan their ID cards at the
                      gate
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        auto_mark_attendance: !prev.auto_mark_attendance,
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      settings.auto_mark_attendance ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.auto_mark_attendance ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Notify Parents */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FiBell className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-slate-800">Notify Parents</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      Send notifications to parents when their child clocks in or out
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        notify_parents_enabled: !prev.notify_parents_enabled,
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      settings.notify_parents_enabled ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.notify_parents_enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Notification Method */}
                {settings.notify_parents_enabled && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Notification Method
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSettings((prev) => ({ ...prev, notify_parents_method: "email" }))
                        }
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                          settings.notify_parents_method === "email"
                            ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        <FiMail className="w-5 h-5" />
                        <div className="text-left">
                          <div className="font-medium text-sm">Email</div>
                          <div className="text-xs opacity-75">Free</div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setSettings((prev) => ({ ...prev, notify_parents_method: "sms" }))
                        }
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                          settings.notify_parents_method === "sms"
                            ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        <FiMessageSquare className="w-5 h-5" />
                        <div className="text-left">
                          <div className="font-medium text-sm">SMS</div>
                          <div className="text-xs opacity-75">₦6 per message</div>
                        </div>
                      </button>
                    </div>
                    {settings.notify_parents_method === "sms" && (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="font-semibold text-amber-800 mb-2">💰 SMS Pricing:</p>
                          <ul className="space-y-1 text-amber-700">
                            <li>
                              • <strong>₦6</strong> per SMS notification
                            </li>
                            <li>• Notifications sent for both clock in and clock out</li>
                            <li>
                              • Estimated: <strong>₦12 per student per day</strong> (2 SMS)
                            </li>
                          </ul>
                        </div>

                        <div className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="font-semibold text-blue-800 mb-2">📊 Processing Fee:</p>
                          <ul className="space-y-1 text-blue-700">
                            <li>
                              • <strong>1.5% + ₦100</strong> for transactions above ₦2,500
                            </li>
                            <li>• Applied to daily SMS charges</li>
                            <li>
                              • <strong>Collection:</strong> Charged daily
                            </li>
                          </ul>
                        </div>

                        <div className="text-xs bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <p className="font-semibold text-purple-800 mb-1">💳 Payment:</p>
                          <p className="text-purple-700">
                            Costs will be automatically deducted from your school's connected
                            payment card daily.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Time Restriction */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FiClock className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-slate-800">Time Restriction</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      Set allowed hours for clock in to prevent early or late entries
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        time_restriction_enabled: !prev.time_restriction_enabled,
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      settings.time_restriction_enabled ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.time_restriction_enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Time Range Selection */}
                {settings.time_restriction_enabled && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Start Hour (24h format)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={settings.time_restriction_start_hour}
                          onChange={(e) =>
                            setSettings((prev) => ({
                              ...prev,
                              time_restriction_start_hour: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                          {settings.time_restriction_start_hour}:00 (
                          {settings.time_restriction_start_hour < 12
                            ? settings.time_restriction_start_hour
                            : settings.time_restriction_start_hour - 12}
                          :00 {settings.time_restriction_start_hour < 12 ? "AM" : "PM"})
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          End Hour (24h format)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={settings.time_restriction_end_hour}
                          onChange={(e) =>
                            setSettings((prev) => ({
                              ...prev,
                              time_restriction_end_hour: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                          {settings.time_restriction_end_hour}:00 (
                          {settings.time_restriction_end_hour < 12
                            ? settings.time_restriction_end_hour
                            : settings.time_restriction_end_hour - 12}
                          :00 {settings.time_restriction_end_hour < 12 ? "AM" : "PM"})
                        </p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <strong>Note:</strong> Students will only be able to clock in between{" "}
                      {settings.time_restriction_start_hour}:00 and{" "}
                      {settings.time_restriction_end_hour}:00
                    </p>
                  </div>
                )}
              </div>

              {/* Settings Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-semibold text-blue-900 text-sm mb-3">Current Configuration</h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${settings.auto_mark_attendance ? "bg-green-500" : "bg-gray-400"}`}
                    ></span>
                    Auto-mark attendance: {settings.auto_mark_attendance ? "Enabled" : "Disabled"}
                  </li>
                  <li className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${settings.notify_parents_enabled ? "bg-green-500" : "bg-gray-400"}`}
                    ></span>
                    Parent notifications:{" "}
                    {settings.notify_parents_enabled
                      ? `Enabled (${settings.notify_parents_method.toUpperCase()})`
                      : "Disabled"}
                  </li>
                  <li className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${settings.time_restriction_enabled ? "bg-green-500" : "bg-gray-400"}`}
                    ></span>
                    Student time restriction:{" "}
                    {settings.time_restriction_enabled
                      ? `Enabled (${settings.time_restriction_start_hour}:00 - ${settings.time_restriction_end_hour}:00)`
                      : "Disabled"}
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer — sticky */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading || fetchingSettings}
            className="px-6 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 font-medium rounded-full transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={loading || fetchingSettings}
            className="inline-flex items-center px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white font-medium rounded-full transition-colors shadow-sm"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClockInOutSettingsModal;
